import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchOfficials, type Official, US_STATES, partyBg } from './apiClient';

interface OfficeHubConfig {
  slug: string;
  title: string;
  officeName: string;
  tagline: string;
  description: string;
  icon: string;
}

const OFFICE_HUBS: OfficeHubConfig[] = [
  {
    slug: 'governors',
    title: 'State Governors',
    officeName: 'Governor',
    tagline: 'The chief executive of every U.S. state',
    description: 'Track the actions, policies, and accountability of the 50 men and women who lead state governments. From executive orders to budget decisions, governors shape the daily lives of millions.',
    icon: '🏛️',
  },
  {
    slug: 'lieutenant-governors',
    title: 'Lieutenant Governors',
    officeName: 'Lieutenant Governor',
    tagline: 'The second-in-command of state government',
    description: 'Lieutenant governors serve as the president of the state senate and are first in line to succeed the governor. In many states they wield significant legislative and policy influence.',
    icon: '⚖️',
  },
  {
    slug: 'attorneys-general',
    title: 'Attorneys General',
    officeName: 'Attorney General',
    tagline: 'The top law enforcement officer in each state',
    description: 'State attorneys general lead lawsuits, defend state laws, and hold corporations and individuals accountable. They are often the most powerful check on abuse within state borders.',
    icon: '⚖️',
  },
  {
    slug: 'secretaries-of-state',
    title: 'Secretaries of State',
    officeName: 'Secretary of State',
    tagline: 'Guardians of elections and democracy',
    description: 'Secretaries of state oversee elections, business registrations, and public records. They are the frontline defenders of democratic integrity and transparency.',
    icon: '📋',
  },
];

function partyShort(party: string | null | undefined): string {
  if (!party) return '?';
  const p = party.toLowerCase();
  if (p.includes('republican')) return 'R';
  if (p.includes('democratic') || p.includes('democrat')) return 'D';
  if (p.includes('independent')) return 'I';
  if (p.includes('libertarian')) return 'L';
  if (p.includes('green')) return 'G';
  return party.charAt(0).toUpperCase();
}

const OfficialRow: React.FC<{ official: Official }> = ({ official }) => {
  return (
    <Link
      to={`/officials/${official.id}`}
      className="block bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-lg p-4 hover:border-gold-500 hover:shadow-md transition group"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-cream-200 dark:bg-navy-700 flex items-center justify-center text-lg font-black text-navy-500 dark:text-cream-300 shrink-0 overflow-hidden">
          {official.photo_url ? (
            <img src={official.photo_url} alt={official.name} className="w-full h-full object-cover rounded-full" />
          ) : (
            (official.name || '?').charAt(0)
          )}
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-black text-navy-700 dark:text-cream-100 group-hover:text-gold-600 transition truncate">
              {official.name}
            </h3>
            {official.party && (
              <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${partyBg(official.party)}`}>
                {partyShort(official.party)}
              </span>
            )}
          </div>
          <p className="text-xs text-navy-400 dark:text-cream-400 truncate">
            {official.office_name} · {official.state}
          </p>
        </div>
        {official.accountability_score != null && (
          <div className="text-right shrink-0">
            <div className="text-lg font-black text-gold-600">{official.accountability_score}</div>
            <div className="text-[9px] uppercase tracking-wider text-navy-400 dark:text-cream-400">Score</div>
          </div>
        )}
      </div>
    </Link>
  );
};

const OfficeHubPage: React.FC<{ config: OfficeHubConfig }> = ({ config }) => {
  const [officials, setOfficials] = useState<Official[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchOfficials({
          office_branch: 'executive',
          level: 'state',
          state: stateFilter || undefined,
          limit: 250,
        });
        const filtered = data.officials.filter((o) => o.office_name === config.officeName);
        filtered.sort((a, b) => (a.state || '').localeCompare(b.state || ''));
        setOfficials(filtered);
      } catch (err: any) {
        setError(err?.message ?? 'Failed to load officials');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [config.officeName, stateFilter]);

  const partyBreakdown = officials.reduce((acc, o) => {
    const p = partyShort(o.party || 'Unknown');
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="text-5xl mb-4">{config.icon}</div>
        <h1 className="text-3xl sm:text-4xl font-black text-navy-700 dark:text-cream-100 mb-2">
          {config.title}
        </h1>
        <p className="text-sm font-bold uppercase tracking-widest text-gold-600 mb-4">
          {config.tagline}
        </p>
        <p className="max-w-2xl mx-auto text-navy-500 dark:text-cream-400 text-sm leading-relaxed">
          {config.description}
        </p>
      </div>

      {/* Stats bar */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        <div className="px-4 py-2 rounded border border-cream-300 dark:border-navy-600 bg-cream-50 dark:bg-navy-800">
          <span className="text-2xl font-black text-navy-700 dark:text-cream-100">{officials.length}</span>
          <span className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-cream-400 ml-2">Officials</span>
        </div>
        {Object.entries(partyBreakdown).map(([party, count]) => (
          <div key={party} className="px-4 py-2 rounded border border-cream-300 dark:border-navy-600 bg-cream-50 dark:bg-navy-800">
            <span className="text-2xl font-black text-navy-700 dark:text-cream-100">{count}</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-cream-400 ml-2">{party}</span>
          </div>
        ))}
      </div>

      {/* State filter */}
      <div className="flex justify-center mb-8">
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-2.5 text-sm font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
        >
          <option value="">All States</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-sm font-bold text-navy-400 dark:text-cream-400 uppercase tracking-wider">Loading officials...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="max-w-2xl mx-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-4 text-center mb-8">
          <p className="text-red-600 dark:text-red-400 text-sm font-bold">{error}</p>
        </div>
      )}

      {/* Official list */}
      {!loading && !error && (
        <div className="grid gap-3 sm:grid-cols-2">
          {officials.map((official) => (
            <OfficialRow key={official.id} official={official} />
          ))}
        </div>
      )}

      {/* Footer CTA */}
      <div className="mt-12 text-center">
        <Link
          to="/officials"
          className="inline-block bg-gold-500 hover:bg-gold-600 text-white font-black uppercase tracking-wider text-sm px-8 py-3 rounded-lg transition"
        >
          Browse All Officials
        </Link>
      </div>
    </div>
  );
};

export { OFFICE_HUBS, OfficeHubPage };
export default OfficeHubPage;
